import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { loginUser } from "../../store/slices/authSlice";
import { fetchAllData } from "../../store/slices/dataSlice";
import { SEO } from "../../components/shared/SEO";

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Paper,
  Chip,
  Alert,
  Collapse,
} from "@mui/material";

import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
  VpnKeyOutlined,
  LockPersonOutlined,
  ArrowForwardRounded,
  ShieldOutlined,
} from "@mui/icons-material";

/* ─── Keyframe animations injected once ─── */
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  @keyframes blobFloat {
    0%,100% { transform: translate(0,0) scale(1); }
    33%      { transform: translate(20px,-20px) scale(1.05); }
    66%      { transform: translate(-15px,15px) scale(0.97); }
  }
  @keyframes cardIn {
    from { opacity:0; transform:translateY(32px) scale(0.96); }
    to   { opacity:1; transform:translateY(0) scale(1); }
  }
  @keyframes shake {
    0%,100% { transform:translateX(0); }
    25%     { transform:translateX(-6px); }
    75%     { transform:translateX(6px); }
  }
  @keyframes particleRise {
    from { transform:translateY(100vh) scale(1); opacity:0; }
    10%  { opacity:1; }
    90%  { opacity:0.4; }
    to   { transform:translateY(-120px) scale(0.5); opacity:0; }
  }
  @keyframes spin { to { transform:rotate(360deg); } }

  .login-card { animation: cardIn 0.55s cubic-bezier(0.34,1.56,0.64,1) both; }
  .error-alert { animation: shake 0.4s ease; }
`;

const PARTICLES = [...Array(12)].map((_, i) => ({
  left: `${8 + i * 8}%`,
  duration: `${8 + (i % 5) * 3}s`,
  delay: `${-i * 1.5}s`,
  size: i % 3 === 0 ? 5 : 3,
  opacity: 0.3 + (i % 3) * 0.12,
}));

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate("/app/dashboard");
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    setError("");
    setEmailError("");
    setPasswordError("");

    let isValid = true;

    if (!email) {
      setEmailError("Email is required.");
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setEmailError("Please enter a valid email address.");
        isValid = false;
      }
    }

    if (!password) {
      setPasswordError("Password is required.");
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      isValid = false;
    }

    if (!isValid) return;

    setLoading(true);

    try {
      const resultAction = await dispatch(loginUser({ email, password }));
      if (loginUser.fulfilled.match(resultAction)) {
        navigate("/app/dashboard");
      } else {
        let errorMsg = "Invalid email or password. Please try again.";
        if (typeof resultAction.payload === "string") {
          errorMsg = resultAction.payload;
        } else if (resultAction.payload && typeof resultAction.payload === "object") {
          errorMsg = (resultAction.payload as any).message || JSON.stringify(resultAction.payload);
        } else if (resultAction.error && resultAction.error.message) {
          errorMsg = resultAction.error.message;
        }
        setError(errorMsg);
      }
    } catch (err) {
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Sign In" description="Sign in to your HRCore secure dashboard." />
      {/* Global keyframes */}
      <style>{GLOBAL_STYLES}</style>

      {/* ── Root ── */}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(145deg, #F0FBFC 0%, #E6FAFC 50%, #F5FEFA 100%)",
          fontFamily: "'Inter', sans-serif",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* ── Animated blobs ── */}
        {[
          {
            w: 480,
            h: 480,
            bg: "radial-gradient(circle,#C9FDF2,#B6F2D1)",
            top: -160,
            left: -120,
            delay: "0s",
          },
          {
            w: 360,
            h: 360,
            bg: "radial-gradient(circle,#B3EBF2,#85D1DB)",
            bottom: -100,
            right: -100,
            delay: "-3s",
          },
          {
            w: 260,
            h: 260,
            bg: "radial-gradient(circle,#B3EBF2,#C9FDF2)",
            bottom: 80,
            left: 60,
            delay: "-6s",
            opacity: 0.28,
          },
        ].map((blob, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: blob.w,
              height: blob.h,
              borderRadius: "50%",
              background: blob.bg,
              filter: "blur(90px)",
              opacity: blob.opacity ?? 0.42,
              animation: `blobFloat 8s ease-in-out infinite`,
              animationDelay: blob.delay,
              top: blob.top,
              bottom: (blob as any).bottom,
              left: blob.left,
              right: (blob as any).right,
              pointerEvents: "none",
            }}
          />
        ))}

        {/* ── Dot grid ── */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle,rgba(133,209,219,0.22) 1px,transparent 1px)",
            backgroundSize: "28px 28px",
            pointerEvents: "none",
          }}
        />

        {/* ── Floating particles ── */}
        {PARTICLES.map((p, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              left: p.left,
              bottom: 0,
              width: p.size,
              height: p.size,
              borderRadius: "50%",
              background: "rgba(133,209,219,0.3)",
              animation: `particleRise ${p.duration} linear infinite`,
              animationDelay: p.delay,
              opacity: p.opacity,
              pointerEvents: "none",
            }}
          />
        ))}

        {/* ── Card ── */}
        <Paper
          className="login-card"
          elevation={0}
          sx={{
            position: "relative",
            zIndex: 10,
            width: "100%",
            maxWidth: 440,
            mx: 2.5,
            px: { xs: 3, sm: 5 },
            py: { xs: 4, sm: 5 },
            borderRadius: "28px",
            background: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(28px)",
            WebkitBackdropFilter: "blur(28px)",
            border: "1px solid rgba(255,255,255,0.95)",
            boxShadow: `
              0 2px 0 0 rgba(255,255,255,0.9) inset,
              0 20px 60px rgba(133,209,219,0.15),
              0 4px 24px rgba(0,0,0,0.06)
            `,
          }}
        >
          {/* ── Header Section ── */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              mb: 4,
            }}
          >
            {/* ── Logo ── */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "18px",
                background: "linear-gradient(135deg,#85D1DB 0%,#B6F2D1 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(133,209,219,0.5)",
                mb: 2,
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: -5,
                  borderRadius: "22px",
                  border: "2px dashed rgba(133,209,219,0.4)",
                },
              }}
            >
              <Typography
                sx={{
                  color: "#0f172a",
                  fontWeight: 900,
                  fontSize: 20,
                  letterSpacing: "-0.5px",
                  fontFamily: "'Inter',sans-serif",
                }}
              >
                HR
              </Typography>
            </Box>

            <Typography
              sx={{
                fontFamily: "'Inter',sans-serif",
                fontWeight: 900,
                fontSize: 24,
                color: "#1e1b4b",
                letterSpacing: "-0.5px",
                lineHeight: 1.2,
              }}
            >
              HRCore
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Inter',sans-serif",
                fontSize: 11,
                color: "#4A9EAA",
                textTransform: "uppercase",
                letterSpacing: "0.15em",
                fontWeight: 700,
                mb: 3,
              }}
            >
              Enterprise HRMS
            </Typography>

            {/* ── Heading ── */}
            <Typography
              variant="h4"
              sx={{
                fontFamily: "'Inter',sans-serif",
                fontWeight: 800,
                color: "#1e1b4b",
                letterSpacing: "-1px",
                mb: 1,
                fontSize: "26px",
              }}
            >
              Welcome back 👋
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Inter',sans-serif",
                fontSize: 14,
                color: "#64748b",
                lineHeight: 1.6,
              }}
            >
              Sign in to access your secure dashboard
            </Typography>
          </Box>

          {/* ── Error alert ── */}
          <Collapse in={!!error} unmountOnExit>
            <Alert
              className="error-alert"
              severity="error"
              icon={false}
              sx={{
                mb: 2.5,
                borderRadius: "12px",
                fontSize: 13,
                fontFamily: "'Inter',sans-serif",
                fontWeight: 500,
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#dc2626",
                py: 1,
              }}
            >
              ⚠️ &nbsp;{error}
            </Alert>
          </Collapse>

          {/* ── Email field ── */}
          <TextField
            id="login-email"
            label="Email Address"
            type="email"
            fullWidth
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            error={!!emailError}
            helperText={emailError}
            placeholder="you@hrcore.in"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlined sx={{ fontSize: 18, color: "inherit" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2.25,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                fontFamily: "'Inter',sans-serif",
                fontSize: 14,
                fontWeight: 500,
                background: "#f8faff",
                transition: "all 0.2s",
                "& fieldset": { borderColor: "#e2e8f0", borderWidth: "1.5px" },
                "&:hover fieldset": { borderColor: "#B3EBF2" },
                "&.Mui-focused fieldset": {
                  borderColor: "#85D1DB",
                  borderWidth: "2px",
                },
                "&.Mui-focused": {
                  background: "#fff",
                  boxShadow: "0 0 0 3px rgba(133,209,219,0.2)",
                },
                "& .MuiInputAdornment-root": { color: "#94a3b8" },
                "&.Mui-focused .MuiInputAdornment-root": { color: "#85D1DB" },
              },
              "& .MuiInputLabel-root": {
                fontFamily: "'Inter',sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: "#475569",
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                "&.Mui-focused": { color: "#85D1DB" },
              },
            }}
          />

          {/* ── Password field ── */}
          <TextField
            id="login-pass"
            label="Password"
            type={showPass ? "text" : "password"}
            fullWidth
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) setPasswordError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            error={!!passwordError}
            helperText={passwordError}
            placeholder="Enter your password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined sx={{ fontSize: 18, color: "inherit" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPass((v) => !v)}
                    tabIndex={-1}
                    edge="end"
                    size="small"
                    sx={{
                      color: "#94a3b8",
                      mr: -0.5,
                      "&:hover": {
                        color: "#85D1DB",
                        background: "rgba(133,209,219,0.15)",
                      },
                    }}
                  >
                    {showPass ? (
                      <VisibilityOff sx={{ fontSize: 18 }} />
                    ) : (
                      <Visibility sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                fontFamily: "'Inter',sans-serif",
                fontSize: 14,
                fontWeight: 500,
                background: "#f8faff",
                transition: "all 0.2s",
                "& fieldset": { borderColor: "#e2e8f0", borderWidth: "1.5px" },
                "&:hover fieldset": { borderColor: "#B3EBF2" },
                "&.Mui-focused fieldset": {
                  borderColor: "#85D1DB",
                  borderWidth: "2px",
                },
                "&.Mui-focused": {
                  background: "#fff",
                  boxShadow: "0 0 0 3px rgba(133,209,219,0.2)",
                },
                "& .MuiInputAdornment-root": { color: "#94a3b8" },
                "&.Mui-focused .MuiInputAdornment-root": { color: "#85D1DB" },
              },
              "& .MuiInputLabel-root": {
                fontFamily: "'Inter',sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: "#475569",
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                "&.Mui-focused": { color: "#85D1DB" },
              },
            }}
          />

          {/* ── Sign In Button ── */}
          <Button
            id="login-submit-btn"
            variant="contained"
            fullWidth
            disableElevation
            disabled={loading}
            onClick={handleLogin}
            endIcon={
              loading ? null : <ArrowForwardRounded sx={{ fontSize: 20 }} />
            }
            sx={{
              height: 52,
              borderRadius: "12px",
              fontFamily: "'Inter',sans-serif",
              fontWeight: 700,
              fontSize: 15,
              letterSpacing: "0.01em",
              textTransform: "none",
              color: "#0f172a",
              background: "linear-gradient(135deg,#85D1DB 0%,#B6F2D1 100%)",
              boxShadow: "0 4px 20px rgba(133,209,219,0.42)",
              transition: "all 0.22s ease",
              "&:hover:not(:disabled)": {
                background: "linear-gradient(135deg,#6BB8C3 0%,#A4E0BF 100%)",
                boxShadow: "0 10px 30px rgba(133,209,219,0.48)",
                transform: "translateY(-2px)",
              },
              "&:active:not(:disabled)": { transform: "translateY(0)" },
              "&.Mui-disabled": { opacity: 0.6, cursor: "not-allowed" },
            }}
          >
            {loading ? (
              <CircularProgress
                size={22}
                thickness={3}
                sx={{ color: "rgba(255,255,255,0.85)" }}
              />
            ) : (
              "Sign In"
            )}
          </Button>

          {/* ── Secure badge ── */}
          <Box
            sx={{
              mt: 3.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
            }}
          >
            <ShieldOutlined sx={{ fontSize: 14, color: "#94a3b8" }} />
            <Typography
              sx={{
                fontFamily: "'Inter',sans-serif",
                fontSize: 11,
                color: "#94a3b8",
                fontWeight: 500,
              }}
            >
              Secured with 256-bit encryption
            </Typography>
            <LockPersonOutlined sx={{ fontSize: 14, color: "#94a3b8" }} />
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default Login;
